import { useEffect, useRef } from "react";
import PDFViewer from "./PDFViewer";
import { ArrowDownTrayIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

export function DocumentViewModal({ documentUrl, mimeType, setIsDocumentViewModalOpen }) {
    const modalRef = useRef(null);
    const { t } = useTranslation("common");

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                closeModal();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const closeModal = () => {
        setIsDocumentViewModalOpen(false);
    }

    const { url, type } = (() => {
        if (
            typeof documentUrl === "string" &&
            documentUrl.startsWith("blob:") &&
            mimeType
        ) {
            if (mimeType === "application/pdf") return { url: documentUrl, type: "pdf" };
            if (mimeType.startsWith("image/")) return { url: documentUrl, type: "image" };
        }
        const lower = typeof documentUrl === "string" ? documentUrl.toLowerCase() : "";
        if (lower.endsWith(".pdf")) return { url: documentUrl, type: "pdf" };
        if (lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png") || lower.endsWith(".gif") || lower.endsWith(".webp"))
            return { url: documentUrl, type: "image" };
        return { url: documentUrl, type: "unknown" };
    })();

    // const handleDownload = () => {
    //     const link = document.createElement('a');
    //     link.href = url;
    //     link.setAttribute('download', ''); // Or a more descriptive filename
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };

    const handleDownload = async () => {
        try {
            const response = await fetch(url, { mode: 'cors' });
            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);

            // Try to keep original filename if available
            let fileName = 'download';

            try {
                const urlObj = new URL(url);
                const pathName = urlObj.pathname;
                const lastSegment = pathName.substring(pathName.lastIndexOf('/') + 1);

                if (lastSegment) {
                    fileName = lastSegment;
                } else if (type === 'image') {
                    fileName = 'image';
                } else if (type === 'pdf') {
                    fileName = 'document.pdf';
                }
            } catch (_) {
                if (type === 'image') fileName = 'image';
                if (type === 'pdf') fileName = 'document.pdf';
            }

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);

            // Fallback to existing behavior
            const link = document.createElement('a');
            link.href = url;
            link.download = '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
        >
            <div ref={modalRef} className="relative bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-auto">
                <div className="sticky top-0 z-10 bg-white bg-opacity-75 backdrop-blur-sm rounded-t-xl p-3 flex justify-end items-center gap-2">
                    <ArrowDownTrayIcon
                        onClick={handleDownload}
                        className="h-6 w-6 text-white cursor-pointer bg-green-800 rounded-md p-1"
                    />
                    <XCircleIcon
                        onClick={closeModal}
                        className="h-6 w-6 text-red-600 hover:text-red-900 cursor-pointer"
                    />
                </div>
                <div className="p-4 flex justify-center items-center -mt-4">
                    {type === "image" ? (
                        <img src={url} alt="Preview" className="max-h-[80vh] rounded-lg object-contain" />
                    ) : type === "pdf" ? (
                        <PDFViewer url={url}></PDFViewer>
                    ) : (
                            <p className="text-center text-gray-600">{t("unableToOpenFileOrFormat")}.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DocumentViewModal;